import { isOptedOutTracking } from './algoliaEventUtils';
import { getGaUserId } from './analytics';

export const ONE_TRUST_GROUPS_UPDATED_EVENT = 'OneTrustGroupsUpdated';

type WaitForConsentAndUserTokenOptions = {
    maxWaitMs: number;
    pollIntervalMs: number;
    getUserToken?: () => string | undefined;
};

type AlgoliaTrackingInitReadinessResult = {
    didOneTrustLoad: boolean;
    hasOptedOut: boolean;
    userToken?: string;
};

const hasUserToken = (userToken: unknown): userToken is string => typeof userToken === 'string' && userToken.length > 0;

const getDefaultGoogleAnalyticsUserToken = () => getGaUserId('_ga');

const waitForNextPoll = (timeoutMs: number): Promise<void> =>
    new Promise<void>(resolve => {
        globalThis.setTimeout(resolve, timeoutMs);
    });

const waitForOneTrustLoad = (timeoutMs: number): Promise<boolean> =>
    new Promise<boolean>(resolve => {
        if (isOneTrustLoaded()) {
            resolve(true);
            return;
        }

        let settled = false;
        const settle = () => {
            if (settled) return;
            settled = true;
            globalThis.removeEventListener(ONE_TRUST_GROUPS_UPDATED_EVENT, onUpdated);
            globalThis.clearTimeout(timeoutId);
            resolve(isOneTrustLoaded());
        };

        const onUpdated = () => settle();
        const timeoutId = globalThis.setTimeout(settle, timeoutMs);
        globalThis.addEventListener(ONE_TRUST_GROUPS_UPDATED_EVENT, onUpdated);
    });

const waitForUserToken = async ({
    pollIntervalMs,
    getUserToken,
    hasExceededMaxWait,
}: {
    pollIntervalMs: number;
    getUserToken: () => string | undefined;
    hasExceededMaxWait: () => boolean;
}): Promise<string | undefined> => {
    while (true) {
        const userToken = getUserToken();

        if (hasUserToken(userToken) || hasExceededMaxWait()) {
            return hasUserToken(userToken) ? userToken : undefined;
        }

        await waitForNextPoll(pollIntervalMs);
    }
};

export const isOneTrustLoaded = (): boolean =>
    window.OnetrustActiveGroups !== undefined || window.OptanonActiveGroups !== undefined;

/** True only when OneTrust has loaded and the user declined tracking consent. */
export const isDefinitivelyOptedOutTracking = (): boolean => isOneTrustLoaded() && isOptedOutTracking();

export const waitForAlgoliaTrackingInitReadiness = async ({
    maxWaitMs,
    pollIntervalMs,
    getUserToken = getDefaultGoogleAnalyticsUserToken,
}: WaitForConsentAndUserTokenOptions): Promise<AlgoliaTrackingInitReadinessResult> => {
    const start = Date.now();
    const hasExceededMaxWait = () => Date.now() - start >= maxWaitMs;

    const didOneTrustLoad = await waitForOneTrustLoad(maxWaitMs);
    if (!didOneTrustLoad) {
        return {
            didOneTrustLoad: false,
            hasOptedOut: false,
        };
    }

    if (isOptedOutTracking()) {
        return {
            didOneTrustLoad: true,
            hasOptedOut: true,
        };
    }

    const userToken = await waitForUserToken({ pollIntervalMs, getUserToken, hasExceededMaxWait });

    // Re-check opt-out after the token poll: the user can revoke consent during
    // the wait (which can run up to maxWaitMs), and the initial check above is
    // now stale. Without this, initAlgoliaTracking would call insights('init')
    // after a mid-wait revocation.
    if (isOptedOutTracking()) {
        return {
            didOneTrustLoad: true,
            hasOptedOut: true,
        };
    }

    return {
        didOneTrustLoad: true,
        hasOptedOut: false,
        userToken,
    };
};

export const waitForInitialSearchUserTokenAfterConsent = async ({
    maxWaitMs,
    pollIntervalMs,
    getUserToken = getDefaultGoogleAnalyticsUserToken,
}: WaitForConsentAndUserTokenOptions): Promise<string | undefined> => {
    const start = Date.now();
    const hasExceededMaxWait = () => Date.now() - start >= maxWaitMs;

    while (true) {
        if (isOneTrustLoaded()) {
            break;
        }

        if (hasExceededMaxWait()) {
            return undefined;
        }

        await waitForNextPoll(pollIntervalMs);
    }

    if (isOptedOutTracking()) {
        return undefined;
    }

    return waitForUserToken({ pollIntervalMs, getUserToken, hasExceededMaxWait });
};
